import React from 'react';
import { Container, Typography, Box, Paper } from '@mui/material';

function Terms() {
  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
          Terms of Service
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Last Updated: November 27, 2025
        </Typography>

        <Box sx={{ '& > *': { mb: 3 } }}>
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              1. Acceptance of Terms
            </Typography>
            <Typography variant="body1" paragraph>
              By accessing or using ComplainAI ("the Service"), you agree to be bound by these Terms of Service. If you do not agree 
              to these terms, do not use the Service. We may modify these terms at any time, and your continued use constitutes 
              acceptance of any changes.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              2. Description of Service
            </Typography>
            <Typography variant="body1" paragraph>
              ComplainAI provides an AI-powered platform that generates dispute letters to help users request refunds or resolution 
              from companies. Our service:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                Uses artificial intelligence to draft personalized dispute letters
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Includes relevant consumer protection law citations
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Provides templates and guidance for sending correspondence
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Tracks dispute status and provides follow-up suggestions
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              3. What ComplainAI Is NOT
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 600, color: 'error.main' }}>
              IMPORTANT: ComplainAI is NOT:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                <strong>A law firm</strong> - We do not provide legal advice, representation, or attorney-client relationships
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                <strong>A credit repair service</strong> - We do not interact with credit bureaus or repair credit reports
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                <strong>A collection agency</strong> - We do not collect debts or interact directly with companies on your behalf
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                <strong>A guarantee of results</strong> - We cannot guarantee that companies will respond or provide refunds
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              4. User Accounts and Eligibility
            </Typography>
            <Typography variant="body1" paragraph>
              To use ComplainAI, you must:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                Be at least 18 years old
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Provide accurate and complete registration information
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Maintain the security of your account credentials
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Not share your account with others
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Notify us immediately of any unauthorized access
              </Typography>
            </Box>
            <Typography variant="body1" paragraph>
              You are responsible for all activities that occur under your account.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              5. Subscription Plans and Payments
            </Typography>
            <Typography variant="body1" paragraph>
              ComplainAI offers multiple subscription tiers:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                <strong>Free Plan:</strong> 1 free dispute to get started
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                <strong>Pro Plan ($9.99/month):</strong> 10 letters per month with AI-enhanced features and auto-escalation
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                <strong>Enterprise Plan ($29.99/month):</strong> Unlimited disputes with priority support
              </Typography>
            </Box>
            <Typography variant="body1" paragraph>
              Subscription fees are billed monthly in advance. You can upgrade, downgrade, or cancel at any time through your account 
              settings. Refunds are provided on a case-by-case basis at our discretion.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              6. Acceptable Use
            </Typography>
            <Typography variant="body1" paragraph>
              You agree NOT to:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                Submit false, fraudulent, or misleading information
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Use the Service for illegal purposes or to harass others
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Attempt to bypass subscription limits or abuse the Service
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Reverse engineer, copy, or exploit our AI technology
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Use automated tools (bots, scrapers) without permission
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Impersonate others or create multiple accounts to circumvent limits
              </Typography>
            </Box>
            <Typography variant="body1" paragraph>
              Violation of these terms may result in immediate account termination without refund.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              7. AI-Generated Content and Accuracy
            </Typography>
            <Typography variant="body1" paragraph>
              ComplainAI uses artificial intelligence (Grok AI) to generate dispute letters. While we strive for accuracy:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                AI-generated letters may contain errors, inaccuracies, or outdated information
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Legal citations are provided for reference but may not apply to your specific situation
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                You are responsible for reviewing, editing, and verifying all content before sending
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                ComplainAI does not guarantee that cited laws are current or applicable to your case
              </Typography>
            </Box>
            <Typography variant="body1" paragraph sx={{ fontWeight: 600, color: 'warning.main' }}>
              ALWAYS review your letter carefully. For legal matters, consult a qualified attorney.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              8. No Guarantees or Warranties
            </Typography>
            <Typography variant="body1" paragraph>
              ComplainAI provides the Service "AS IS" without warranties of any kind, including:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                No guarantee that companies will respond to your letters
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                No guarantee that you will receive refunds or favorable outcomes
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                No warranty that the Service will be error-free or uninterrupted
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                No warranty regarding the accuracy or completeness of AI-generated content
              </Typography>
            </Box>
            <Typography variant="body1" paragraph>
              We are a tool to assist you in creating correspondence. Success depends on many factors outside our control.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              9. Limitation of Liability
            </Typography>
            <Typography variant="body1" paragraph>
              To the maximum extent permitted by law, ComplainAI and its officers, directors, employees, and affiliates shall not be 
              liable for:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                Any indirect, incidental, special, or consequential damages
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Loss of profits, revenue, data, or business opportunities
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Damages resulting from use or inability to use the Service
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Actions taken based on AI-generated content
              </Typography>
            </Box>
            <Typography variant="body1" paragraph>
              Our total liability shall not exceed the amount you paid for the Service in the 12 months preceding the claim.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              10. Indemnification
            </Typography>
            <Typography variant="body1" paragraph>
              You agree to indemnify and hold ComplainAI harmless from any claims, damages, losses, or expenses (including attorney fees) 
              arising from:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                Your use of the Service
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Your violation of these Terms
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Your violation of any rights of third parties
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Content you submit to the Service
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              11. Dispute Resolution and Arbitration
            </Typography>
            <Typography variant="body1" paragraph>
              Any disputes arising from these Terms or your use of ComplainAI shall be resolved through binding arbitration in 
              San Francisco, California, under the rules of the American Arbitration Association. You waive the right to participate 
              in class actions or jury trials.
            </Typography>
            <Typography variant="body1" paragraph>
              These Terms are governed by the laws of the State of California, without regard to conflict of law principles.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              12. Termination
            </Typography>
            <Typography variant="body1" paragraph>
              We may suspend or terminate your account at any time for:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                Violation of these Terms
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Fraudulent or abusive behavior
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Non-payment of subscription fees
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Any reason at our sole discretion
              </Typography>
            </Box>
            <Typography variant="body1" paragraph>
              You may cancel your account at any time through your account settings. Upon termination, your access to the Service 
              will cease, but these Terms will continue to apply to past use.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
              13. Contact Information
            </Typography>
            <Typography variant="body1" paragraph>
              For questions about these Terms of Service, contact us at:
            </Typography>
            <Typography variant="body1" paragraph>
              Email: legal@complainai.app<br />
              Address: ComplainAI, Inc., 123 Legal Street, Suite 100, San Francisco, CA 94103
            </Typography>
          </Box>

          <Box sx={{ mt: 6, p: 3, bgcolor: 'background.paper', border: 2, borderColor: 'primary.main', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              Summary (Not Legal Advice)
            </Typography>
            <Typography variant="body2" paragraph>
              ✗ We're a tool to help you write dispute letters<br />
              ✗ We're NOT lawyers and don't provide legal advice<br />
              ✗ Review all AI-generated content before using it<br />
              ✗ We can't guarantee results or refunds<br />
              ✗ You're responsible for how you use our Service<br />
              ✗ Be honest and don't abuse the platform
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}

export default Terms;
